import React from "react";
import { Trans } from "@lingui/macro";
import { Link } from "react-router-dom";
import { Transition } from "@headlessui/react";
import { Alert } from "../../../UI/Alert/Alert";
import { ROUTS } from "../../../../routs/routs";
import { Loader } from "../../../UI/Loader/Loader";
import { Button } from "../../../UI/Button/Button";
import { getCookie } from "../../../../utils/utils";
import { GifLogo } from "../../../UI/GifLogo/GifLogo";
import { useDispatch, useSelector } from "react-redux";
import { CONTACTS } from "../../../../constants/contacts";
import { InputEmail } from "../../../UI/InputEmail/InputEmail";
import { useInputChange } from "../../../../hooks/useInputChange";
import { InputPassword } from "../../../UI/InputPassword/InputPassword";
import { InputCheckbox } from "../../../UI/InputCheckbox/InputCheckbox";
import { useCheckboxChange } from "../../../../hooks/useCheckboxChange";
import {
  selectAuthError,
  selectAuthLoader,
} from "../../../../model/system/selectors";
import {
  authSagaAC,
  authErrorAC,
} from "../../../../model/system/systemActionCreators";

// TODO: get language settings from server after authorization
export const SignInContainer: React.FC = () => {
  const dispatch = useDispatch();

  const authError = useSelector(selectAuthError);
  const authLoader = useSelector(selectAuthLoader);

  const urlLinkSupport: string = `mailto:${CONTACTS.SUPPORT_EMAIL}`;
  const urlForgotPasswordPage: string = ROUTS.FORGOT_PASSWORD_PAGE;
  const urlHomePage: string = ROUTS.HOME_PAGE;

  const { value: emailValue, changeValueHandler: emailChangeValueHandler } =
    useInputChange(getCookie("tindaRememberMeLogin") || "");

  const {
    value: passwordValue,
    setValue: setPasswordValue,
    changeValueHandler: passwordChangeValueHandler,
  } = useInputChange("");

  const {
    value: rememberChecked,
    changeValueHandler: rememberChangeCheckedHandler,
  } = useCheckboxChange(true);

  React.useEffect(() => {
    if (authError) {
      dispatch(authErrorAC(false));
    }

    // eslint-disable-next-line
  }, [emailValue, passwordValue]);

  const onSubmitHandler = (e: React.FormEvent) => {
    e.preventDefault();

    dispatch(authSagaAC(emailValue, passwordValue, rememberChecked));

    setPasswordValue("");
  };

  let appName = process.env.REACT_APP_CUSTOMER;

  if (
    process.env.REACT_APP_CUSTOMER === "SKAPPS" ||
    process.env.REACT_APP_CUSTOMER === "SKAPPSK"
  ) {
    appName = "SKapps";
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <GifLogo />
        <h2 className="mt-5 text-center text-3xl font-extrabold text-gray-900">
          <Trans>Log in to your</Trans> {appName}
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          <Trans values={{ urlLinkSupport }}>
            If you can't to log in,{" "}
            <a
              href={urlLinkSupport}
              className="font-medium text-primary-800 hover:text-primary-900"
            >
              contact support
            </a>
          </Trans>
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="relative bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {authError ? (
            <Transition
              show={authError}
              leaveTo="opacity-0"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leaveFrom="opacity-100"
              enter="transition-opacity duration-500"
              leave="transition-opacity duration-500"
            >
              <Alert
                type="error"
                className="mb-4"
                title={<Trans>Wrong email or password.</Trans>}
                onCloseButton={() => dispatch(authErrorAC(false))}
              />
            </Transition>
          ) : null}

          <form
            method="POST"
            action={urlHomePage}
            className="space-y-6"
            onSubmit={onSubmitHandler}
          >
            <div>
              <InputEmail
                id="email"
                name="email"
                required={true}
                value={emailValue}
                label={<Trans>Email</Trans>}
                onChange={emailChangeValueHandler}
              />
            </div>

            <div>
              <InputPassword
                id="password"
                name="password"
                required={true}
                value={passwordValue}
                label={<Trans>Password</Trans>}
                onChange={passwordChangeValueHandler}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <InputCheckbox
                  id="remember_me"
                  value="remember"
                  name="remember_me"
                  checked={rememberChecked}
                  label={<Trans>Remember me</Trans>}
                  onChange={rememberChangeCheckedHandler}
                />
              </div>

              <div className="text-sm hidden">
                <Link
                  to={urlForgotPasswordPage}
                  className="font-medium text-indigo-600 hover:text-indigo-500"
                >
                  <Trans>Forgot your password?</Trans>
                </Link>
              </div>
            </div>

            <div>
              <Button
                type="submit"
                buttonClassName="w-full"
                label={<Trans>Sign in</Trans>}
              />
            </div>
          </form>

          {authLoader ? <Loader /> : null}
        </div>
      </div>
    </div>
  );
};
